import React, { memo, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import cx from 'clsx';

import { IconRcDefaultNft, IconDefaultToken } from '@/assets/icon/token';
import { useDeviceTypeIsMobile } from '@/hooks';
import { getTokenSymbol, formatPrice, formatTokenNum, formatNetworth } from '@/utils';
import { NftMoreDropdown } from '@/views/NFTs';
import { NftToken } from '@/views/NFT';

import { ProposalFlag } from '../ProposalFlag';
import { LazyMedia } from '../LazyMedia';
import style from './index.module.scss';

type TokenWithAmountProps = {
  // 有时候找不到 token，必须用 id 来判断是否是 nft
  id: string;
  token?: Tokens;
  changeType?: string; // '+' | '-'
  className?: string;
  iconClassName?: string;
  onTokenClick?: (args: { id: string; name: string; chain: string; uuid: string }) => void;
  onNftRefresh?: (data?: NftToken) => void;
  hidePrice?: boolean;
  amount: number;
  // 从外面传进来的，就不用再算了
  name?: string;
  // 当时的价格
  price?: number;
  iconSize?: number;
};

export const TokenWithAmount = memo((props: TokenWithAmountProps) => {
  const {
    id,
    token,
    changeType,
    className,
    iconClassName,
    onTokenClick,
    onNftRefresh,
    hidePrice = false,
    name,
    price,
    amount,
    iconSize = 20,
  } = props;

  const isMobile = useDeviceTypeIsMobile();

  const _isNft = id.length === 32;
  const _price = price || token?.price || 0; // 当时的价格 || 此时的价格 || 0

  const _amount = useMemo(() => (_isNft ? amount : formatTokenNum(amount)), [amount, _isNft]);
  const _name = useMemo(() => {
    if (name) {
      return name;
    }

    const innerSymbol = getTokenSymbol(token);

    return !_isNft
      ? innerSymbol
      : token?.name || (innerSymbol ? `${innerSymbol} ${token?.inner_id}` : 'Unknown NFT');
  }, [_isNft, name, token]);

  const handleNftRefresh = useCallback(res => onNftRefresh?.(res?.nft), [onNftRefresh]);

  const handleTokenClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const { id, name, tokenChain } = e.currentTarget.dataset;
      if (!id) {
        return;
      }

      if (!_isNft) {
        onTokenClick?.({
          id,
          name: name!,
          chain: tokenChain!,
          uuid: `${tokenChain}_token:${id}`,
        });
      }
    },
    [onTokenClick, _isNft],
  );

  const iconStyle = useMemo(() => ({ width: iconSize, height: iconSize }), [iconSize]);

  return (
    <div
      className={cx(
        style.tokenWithAmount,
        isMobile && style.isMobile,
        changeType === '+' && style.income,
        !!onTokenClick && !_isNft && style.clickable,
        className,
      )}
      data-id={id}
      data-name={name}
      data-token-chain={token?.chain}
      onClick={handleTokenClick}>
      {_isNft ? (
        <LazyMedia
          failedPlaceholder={<IconRcDefaultNft />}
          src={token?.content}
          type={token?.content_type}
          className={cx(style.tokenIcon, style.isNft, iconClassName)}
          style={iconStyle}
          hoverChildren={
            onNftRefresh && token ? (
              <NftMoreDropdown
                chainId={token?.chain}
                nftId={id}
                className={style.moreNftOp}
                onSuccess={handleNftRefresh}
              />
            ) : null
          }
          hoverShow
        />
      ) : (
        <LazyMedia
          failedPlaceholder={<img src={IconDefaultToken} alt="logo" />}
          className={cx(style.tokenIcon, iconClassName)}
          style={iconStyle}
          src={token?.logo_url}
        />
      )}
      <div className={cx(style.tokenTitle, !!onTokenClick && !_isNft && style.isClickable)}>
        {changeType}
        {_amount}
        <span className={style.tokenName}>{_name}</span>
        {_isNft || hidePrice ? null : (
          <span className={style.tokenPrice}>
            ({formatNetworth(_price * amount)}
            <span className={style.tokenUtilPrice}>@{formatNetworth(_price)}</span>)
          </span>
        )}
      </div>
    </div>
  );
});

export const TokenWithAmountProposal = (
  props: TokenWithAmountProps & { time?: number; flagOffset?: number },
) => {
  const { id, token, price, time, flagOffset = 8, ...rest } = props;

  const _isNft = id.length === 32;

  const _price = price || token?.price || 0; // 当时的价格 || 此时的价格 || 0
  const _priceStr = time
    ? ` (price was ${formatPrice(_price)} on ${dayjs.unix(time).format('YYYY/MM/DD')})`
    : '';

  const _name = useMemo(() => {
    const innerSymbol = getTokenSymbol(token);

    return !_isNft
      ? innerSymbol
      : token?.name || (innerSymbol ? `${innerSymbol} ${token?.inner_id}` : 'Unknown NFT');
  }, [_isNft, token]);

  return (
    <ProposalFlag
      key={id}
      initType={_isNft ? 'NFT' : 'Token'}
      initAddrs={_isNft ? token?.contract_id : id}
      offsetX={flagOffset}
      offsetY={4}
      appendDescription={{ 'Token price error': _priceStr }}
      initExtra={_name}>
      <div className={style.proposalFlag}>
        <div className={style.hoverSpace} />
        <TokenWithAmount id={id} token={token} price={_price} name={_name} {...rest} />
      </div>
    </ProposalFlag>
  );
};
